import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  buildCrudRouteEditFormClearPropsFromHistoryEntry,
  buildDrawerReopenPayload,
  clearUserActionsHistory,
  companyIdSelector,
  currentUserIdSelector,
  EntitiesRecordAvailabilityApi,
  errors,
  filterToDrawerHistoryItems,
  getRecordAvailabilityParamsForHistoryEntry,
  IUserActionHistoryEntry,
  Log,
  readUserActionsHistory,
  useDrawers,
  USER_ACTIONS_HISTORY_DRAWER_PAGE_SIZE,
  UserActionHistoryOrigin,
} from "@ryde/shared";

export const useUserActionsHistoryDrawer = (onCloseDrawer: () => void) => {
  const { t } = useTranslation();
  const userId = useRecoilValue(currentUserIdSelector);
  const companyId = useRecoilValue(companyIdSelector);
  const { openDrawer, clearCrudEditDrawerAtomsBeforeOpen } = useDrawers();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [storageTick, setStorageTick] = useState<number>(0);
  const listSurfaceRef = useRef<HTMLDivElement>(null);

  const cardItems = useMemo((): IUserActionHistoryEntry[] => {
    if (!userId || !companyId) {
      return [];
    }
    const historyRevision: number = storageTick;
    const rawItems = readUserActionsHistory({ userId, companyId }).items;
    const itemsAfterRevisionGate = rawItems.filter(() => historyRevision >= 0);
    return filterToDrawerHistoryItems(itemsAfterRevisionGate);
  }, [userId, companyId, storageTick]);

  const pageCount = Math.max(
    1,
    Math.ceil(cardItems.length / USER_ACTIONS_HISTORY_DRAWER_PAGE_SIZE),
  );
  const safePage = Math.min(page, pageCount);

  const slice = useMemo(() => {
    const start = (safePage - 1) * USER_ACTIONS_HISTORY_DRAWER_PAGE_SIZE;
    return cardItems.slice(
      start,
      start + USER_ACTIONS_HISTORY_DRAWER_PAGE_SIZE,
    );
  }, [cardItems, safePage]);

  const refresh = useCallback(() => {
    setStorageTick(v => v + 1);
  }, []);

  const onClear = useCallback(() => {
    if (!userId || !companyId) {
      return;
    }
    clearUserActionsHistory({ userId, companyId });
    refresh();
  }, [userId, companyId, refresh]);

  const clearRouteCardEditFormCrudAtomsIfNeeded = useEvent(
    (entry: IUserActionHistoryEntry) => {
      const clearProps =
        buildCrudRouteEditFormClearPropsFromHistoryEntry(entry);
      if (clearProps) {
        clearCrudEditDrawerAtomsBeforeOpen(clearProps);
      }
    },
  );

  const onClickHandler = useEvent(async (entry: IUserActionHistoryEntry) => {
    const availabilityParams =
      getRecordAvailabilityParamsForHistoryEntry(entry);
    if (availabilityParams) {
      try {
        const { isRecordAvailable } =
          await EntitiesRecordAvailabilityApi.check(availabilityParams);
        if (!isRecordAvailable) {
          Log.warnAndToast(t(errors.common.recordNotAvailable));
          onCloseDrawer();
          return;
        }
      } catch (e) {
        Log.debug("History reopen: record availability check failed", e);
        Log.warnAndToast(t(errors.common.technicalProblemOccurred));
        return;
      }
    }

    if (entry.origin === UserActionHistoryOrigin.RouteCard) {
      clearRouteCardEditFormCrudAtomsIfNeeded(entry);
      navigate(`${entry.pathname}${entry.search}`);
      onCloseDrawer();
      return;
    }

    if (!entry.drawerType) {
      return;
    }

    const drawerProps = buildDrawerReopenPayload(
      entry.drawerType,
      entry.drawerPayload,
    );
    Log.debug("Open drawer from history with props:", drawerProps);
    openDrawer({ drawerProps });
    onCloseDrawer();
  });

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    listSurfaceRef.current?.scrollTo({ top: 0 });
  }, [safePage]);

  return {
    page: safePage,
    setPage,
    pageCount,
    slice,
    onClear,
    onClickHandler,
    hasUserContext: Boolean(userId && companyId),
    totalCards: cardItems.length,
    listSurfaceRef,
  };
};
